/**
 * @file   src\components\Onboarding\Dashboard\LeftpanelTabContentRight.js
 * @brief  Match tab left panel - property selector + demand insights
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { filterContext } from "../../../pages/v2/Home";
import { USER_TYPE_AGENT, BUY } from "../../../constants/common";
import {
  getLocalStorage,
  setLocalStorage,
  removeLocalStorage,
  decodeParam,
} from "../../../helpers/common";
import { USER_INFO_KEY } from "../../../constants/common";
import { usePrevious } from "../../../hooks/usePrevious";
import { getMatchClientList } from "../../../services/agentClientsService";
import { renameKey, combineKey } from "../../../helpers/common";
import { getInventoryListByUserId } from "../../../services/inventoryService";
import { isGitchaOwnerView } from "../../../helpers/tenant";
import { fetchUserDetails } from "../../../actions/userprofile";
import { useSearchParams } from "react-router-dom";
import AddProperty from "./AddProperty";
import DemandTeaser from "./DemandTeaser";
import PropertySelectorDropdown from "./PropertySelectorDropdown";
import MatchDemandPanel from "./MatchDemandPanel";
import { getMyMlsListings } from "../../../services/v2/mlsListings";
import useAiSyncStatus from "@/hooks/useAiSyncStatus";
// The selector is the only way to pick a property from the Feed, so a page
// cap here made an agent's remaining listings unreachable, and the group
// header reported the cap as if it were their listing count.
const MLS_SELECTOR_LIMIT = 1000;
import { mlsPropertyId } from "../../../helpers/matchTabRedirect";
import { trackEvent } from "../../../services/analytics";
import { isInAllowedStates } from "../../../helpers/propertyTenantScope";
import { useAppConfig } from "@/appConfig";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import { useTourDemand } from "@/components/MemberTour/seeds";
import { useMarkOnboardingMoment, useOnboardingProgress } from "@/hooks/useOnboardingProgress";

const LeftpanelTabContentRight = (props) => {
  const dispatch = useDispatch();
  const filterCntxt = useContext(filterContext);
  const [searchParams] = useSearchParams();
  const invIdfromUrl = searchParams.get("InvId");
  const clientIdFromUrl = searchParams.get("ClientId");
  // Plain (unencoded) - set by matchTabRedirect.js for the inventory/MLS
  // "Matches" deep link, distinct from the base64 InvId used by notification
  // email links below.
  const selectInvIdFromUrl = searchParams.get("selectInvId");

  const decodedInvId = invIdfromUrl ? decodeParam(invIdfromUrl) : null;
  const decodedClientId = clientIdFromUrl ? decodeParam(clientIdFromUrl) : null;
  const targetInvId = decodedInvId ?? selectInvIdFromUrl;

  const previouslySelectedClient = getLocalStorage("MatchSelectedClient");
  // Match tab demand insights - read match data from filterContext
  const { adType, adListGitchaGenie, totalCountMatch, setMatchInventory } =
    filterCntxt;
  const sessionData = getLocalStorage(USER_INFO_KEY);
  const userType = _.get(sessionData, "userInfo.userType", "");
  const userId = _.get(sessionData, "userInfo.userId", "");
  const [propertyList, setPropertyList] = useState(null);
  const [mlsPropertyList, setMlsPropertyList] = useState(null);
  const [hasFetchedProperties, setHasFetchedProperties] = useState(false);
  const [hasFetchedMls, setHasFetchedMls] = useState(false);
  // Set once every list has loaded and a "Matches" deep link still named a
  // property that isn't in them. Reported once per link.
  const [unresolvedLink, setUnresolvedLink] = useState(false);
  const reportedLinkRef = useRef(null);
  // A property just created by quick add, waiting for the refreshed list to
  // contain it so it can be selected as a full row.
  const [pendingInvId, setPendingInvId] = useState(null);
  const { flags, tenant } = useAppConfig();
  const aiSync = useAiSyncStatus();
  const showMlsListings = !!flags?.mlsListings;
  const [clientList, setClientList] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const userDetailsFromStore = useSelector(
    (state) => state.userProfile.userDetails,
  );
  const [userDetails, setUserDetails] = useState(null);
  const {
    updateFilterGitchaGenie,
    selectedProperty,
    setSelectedProperty,
    gaugeDemand,
    setGaugeDemand,
    onDismiss,
  } = props;
  const key = getLocalStorage("FilterTabKey");
  const [isPremiumUser, setIsPremiumUser] = useState(null);

  const prevAdType = usePrevious(adType);
  const prevInventoryId = usePrevious(selectedProperty?.Inv_Id);
  const [reload, setReload] = useState(0);

  const allProperties = useMemo(() => {
    const merged = [...(propertyList || []), ...(mlsPropertyList || [])];
    return merged.length ? merged : null;
  }, [propertyList, mlsPropertyList]);

  // True once every property source this tenant uses has resolved.
  const allFetched = hasFetchedProperties && (!showMlsListings || hasFetchedMls);

  // A "Matches" deep link is resolved against lists that load after mount, so
  // until they have the left panel would show the idle teaser as if the click
  // did nothing.
  const deepLinkPending = targetInvId != null && !allFetched;

  // totalCountMatch stays null until the first match fetch resolves; treat the
  // property load and pending first fetch as loading so we skeleton, not empty.
  // totalCountMatch only resolves once a match fetch runs, and match mode is
  // opt-in now, so without the gaugeDemand guard this skeletons forever.
  const demandLoading =
    gaugeDemand && (!allFetched || (!!allProperties && totalCountMatch == null));
  const tourDemand = useTourDemand();
  const showDemand = Boolean(tourDemand) || Boolean(gaugeDemand && selectedProperty);
  const isAgentViewer = userType === USER_TYPE_AGENT;
  const { isGitchaDefaultWebsite } = useCurrentWebsite();
  const onboarding = useOnboardingProgress(isAgentViewer && !isGitchaDefaultWebsite).data;
  const markOnboardingMoment = useMarkOnboardingMoment();
  const demandMarkedRef = useRef(false);
  const ownDemandShown = Boolean(gaugeDemand && selectedProperty && !tourDemand && !demandLoading);
  useEffect(() => {
    if (!ownDemandShown || !onboarding || demandMarkedRef.current) return;
    if (onboarding.steps?.find((step) => step.id === "seeDemand")?.done !== false) return;
    // The progress query refetches on window focus, which re-runs this effect
    // while the save is still in flight; send it once per mount.
    demandMarkedRef.current = true;
    markOnboardingMoment.mutate(onboarding.pages?.demand || 1005);
  }, [ownDemandShown, onboarding]);

  useEffect(() => {
    setMatchInventory?.((prev) =>
      prev.resolved === allFetched && prev.hasProperties === !!allProperties
        ? prev
        : { resolved: allFetched, hasProperties: !!allProperties },
    );
  }, [allFetched, allProperties, setMatchInventory]);

  useEffect(() => {
    getClientList();
    if (userId) {
      dispatch(fetchUserDetails());
    }
  }, [key, adType, reload]);

  useEffect(() => {
    if (userDetailsFromStore !== null && userDetailsFromStore?.length > 0) {
      setUserDetails(userDetailsFromStore[0]);
      if (userDetailsFromStore[0]?.period !== 1 && userType === 2) {
        setIsPremiumUser(true);
        setLocalStorage("isPremiumUser", 2); // 2 for premium
      } else {
        setIsPremiumUser(false);
        setLocalStorage("isPremiumUser", 1); // 1 for non premium
      }
    } else {
      setUserDetails(null);
    }
  }, [userDetailsFromStore]);

  const updateReloadParam = () => {
    setReload(reload + 1);
  };

  // Not gated on selectedClient: consumers have no client list, and gating
  // left hasFetchedProperties false forever.
  useEffect(() => {
    if (selectedClient) {
      setLocalStorage("MatchSelectedClient", selectedClient);
    }
    getInventoryList();
  }, [adType, selectedClient]);

  useEffect(() => {
    if (clientList && clientList?.length > 0) {
      if (decodedClientId) {
        const clientFromUrl = clientList?.find(
          (client) => client.value == decodedClientId,
        );
        if (clientFromUrl) {
          setSelectedClient(clientFromUrl);
        } else {
          setSelectedClient(clientList[0]);
        }
      } else if (previouslySelectedClient) {
        const previousClient = clientList?.find(
          (client) => client.value == previouslySelectedClient.value,
        );
        if (previousClient) {
          setSelectedClient(previousClient);
        } else {
          setSelectedClient(clientList[0]);
        }
      } else {
        setSelectedClient(clientList[0]);
      }
    }
  }, [clientList, decodedClientId]);

  useEffect(() => {
    if (prevInventoryId != selectedProperty?.Inv_Id || adType != prevAdType) {
      setLocalStorage("selectedPropertyId", selectedProperty);
      const isMls = selectedProperty?.rowType === "mls";
      updateFilterGitchaGenie({
        inventoryId: isMls ? null : selectedProperty?.Inv_Id,
        mlsListingKey: isMls ? selectedProperty?.ListingKey : null,
        city: selectedProperty?.City,
        state: selectedProperty?.State,
      });
    }
  }, [selectedProperty]);

  // Select a just-created property the moment the refreshed list carries it,
  // and drop into match mode - adding it and asking "who wants this" are the
  // same intent.
  useEffect(() => {
    if (pendingInvId == null || !allProperties?.length) return;
    const created = allProperties.find(
      (property) => property.Inv_Id == pendingInvId,
    );
    if (!created) return;
    setSelectedProperty(created);
    if (setGaugeDemand) setGaugeDemand(true);
    setPendingInvId(null);
  }, [pendingInvId, allProperties, setGaugeDemand]);

  // Tells the agent the link didn't resolve, and records why, so an
  // intermittent "the Matches pill did nothing" report arrives with a cause.
  const reportUnresolvedLink = () => {
    setUnresolvedLink(true);
    if (reportedLinkRef.current === targetInvId) return;
    reportedLinkRef.current = targetInvId;
    const isMlsLink = String(targetInvId).startsWith("mls:");
    trackEvent({
      name: "match_deeplink_unresolved",
      attributes: {
        reason: isMlsLink && !showMlsListings ? "mls_not_enabled" : "not_in_list",
        is_mls: isMlsLink,
        ad_type: adType,
        // The Matches pill sends selectInvId; notification emails send InvId.
        source: decodedInvId ? "notification" : "matches_link",
        target_id: String(targetInvId),
      },
    });
  };

  // Once the agent has a property selected the message is stale; Home can also
  // clear it from outside this panel, which must not bring the message back.
  useEffect(() => {
    if (selectedProperty) setUnresolvedLink(false);
  }, [selectedProperty]);

  useEffect(() => {
    // Nothing is selected by default. Matching is an intent the agent
    // expresses by picking a property - auto-selecting the first one meant
    // the feed flipped to matches before they asked for anything, and the two
    // modes fought each other. Only an explicit deep link pre-selects.
    if (allProperties && allProperties.length > 0) {
      if (targetInvId != null) {
        const invFromUrl = allProperties.find(
          (property) => property.Inv_Id == targetInvId,
        );
        if (invFromUrl) {
          setSelectedProperty(invFromUrl);
          // Both deep links (selectInvId and the match notification InvId)
          // mean "show this property's matches now" - without this the
          // property gets selected but the feed stays on the general market
          // view.
          if (setGaugeDemand) setGaugeDemand(true);
          setUnresolvedLink(false);
        } else if (allFetched) {
          reportUnresolvedLink();
          // The agent followed an explicit link (a notification email, a
          // "Matches" deep link) expecting a property to be selected. If it
          // isn't in allProperties - deleted, or filtered out by tenant
          // locationRestriction scoping - silently doing nothing leaves
          // whatever was selected before dangling on screen with no
          // indication the link didn't resolve. Clearing it routes to
          // whichever "no property selected" empty state already exists.
          setSelectedProperty(null);
        }
      }
    } else if (allFetched) {
      if (targetInvId != null) reportUnresolvedLink();
      removeLocalStorage("selectedPropertyId");
      setSelectedProperty(null);
    }
  }, [
    allProperties,
    targetInvId,
    selectInvIdFromUrl,
    hasFetchedProperties,
    hasFetchedMls,
    showMlsListings,
  ]);

  /**
   * To fetch inventory details
   */
  const getInventoryList = async () => {
    if (!userId) {
      setPropertyList(null);
      setHasFetchedProperties(true);
      return;
    }
    await getInventoryListByUserId({ adType })
      .then((response) => {
        const responseResult = _.get(response, "result", null);
        if (response.errorCode === 0 && responseResult) {
          // The agent's inventory is not MLS-scoped, so a multi-market agent
          // saw properties from other states in this tenant's selector. They
          // can never match here - this feed only carries this MLS's
          // Want-Listings - so scope them to the tenant's markets.
          const allowedStates = tenant?.locationRestriction?.enabled
            ? tenant?.locationRestriction?.states
            : null;
          const scoped = responseResult.filter((p) =>
            isInAllowedStates(p, allowedStates),
          );
          if (scoped.length > 0) {
            setPropertyList(scoped);
          } else {
            setPropertyList(null);
          }
        }
      })
      .catch((error) => {
        console.error("[Match] inventory error:", error);
      })
      .finally(() => {
        setHasFetchedProperties(true);
      });
  };
  // Not gated on match mode. The property selector is how an agent ENTERS
  // match mode, so waiting for key === "match" meant MLS listings were only
  // fetched once a property had already been picked - and since they were
  // missing from the list, they could never be the one picked. A cold feed
  // showed prospect properties only.
  useEffect(() => {
    if (userId && showMlsListings && adType === BUY) {
      getMlsProperties();
    } else {
      setMlsPropertyList(null);
      setHasFetchedMls(true);
    }
  }, [adType, showMlsListings, userId]);

  // The list is fetched once on mount, so during onboarding it kept showing
  // the counts from before scoring started while the banner counted up beside
  // it. aiSync.settled advances on the banner's own poll, so refetching on it
  // costs no extra polling and only fires when something actually settled.
  useEffect(() => {
    if (!userId || !showMlsListings || adType !== BUY) return;
    if (!aiSync.isSyncing) return;
    getMlsProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiSync.settled]);

  const getMlsProperties = async () => {
    await getMyMlsListings({
      page: 1,
      limit: MLS_SELECTOR_LIMIT,
      sortType: "NEWEST",
      // Mid-sync the picker offers only scored listings; the backend
      // ignores this once the sync completes.
      readyOnly: true,
    })
      .then((response) => {
        const rows = _.get(response, "data.data", []);
        const adapted = (rows || []).map((r) => ({
          ...r,
          rowType: "mls",
          Inv_Id: mlsPropertyId(r.ListingKey),
        }));
        setMlsPropertyList(adapted.length ? adapted : null);
      })
      .catch((error) => {
        console.error("[Match] mls listings error:", error);
      })
      .finally(() => {
        setHasFetchedMls(true);
      });
  };

  /**
   * To fetch client details for agents
   */
  const getClientList = async () => {
    if (userId && key == "match") {
      await getMatchClientList()
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            responseResult?.ClientList.forEach((obj) =>
              renameKey(obj, "ClientId", "value"),
            );
            responseResult?.ClientList.forEach((obj) =>
              combineKey(obj, "FirstName", "LastName", "label"),
            );
            setClientList(responseResult?.ClientList);
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };

  // Adding the property and asking who wants it are one intent, so the new row
  // is selected once the refreshed list carries it - the id, not the modal's
  // return, since everything downstream expects a full inventory row. Shared by
  // both AddProperty mounts: the empty-inventory one is an agent's first
  // property, which is exactly where this matters.
  const handlePropertyAdded = (prop) => {
    if (prop?.Inv_Id != null) setPendingInvId(prop.Inv_Id);
    getInventoryList();
  };

  return (
    <>
      <div className="filter-properties-wrap pb-20">
        {!allFetched ? (
          <div className="match-property-skeleton shimmer" />
        ) : (
          <PropertySelectorDropdown
            propertyList={allProperties || []}
            selectedProperty={selectedProperty}
            setSelectedProperty={setSelectedProperty}
            onClear={() => {
              setUnresolvedLink(false);
              setSelectedProperty(null);
              if (setGaugeDemand) setGaugeDemand(false);
            }}
            onUserSelect={(prop) =>
              setGaugeDemand && setGaugeDemand(Boolean(prop))
            }
            adType={adType}
            userType={userType}
            selectedClient={selectedClient}
            userDetails={userDetails}
            updateReloadParam={updateReloadParam}
          />
        )}

        {allFetched ? (
          <AddProperty
            compact
            existingProperties={allProperties || []}
            onSelectExisting={(prop) => {
              setSelectedProperty(prop);
              if (setGaugeDemand) setGaugeDemand(true);
            }}
            onPropertyAdded={handlePropertyAdded}
            isAgent={userType === USER_TYPE_AGENT}
            selectedClient={selectedClient}
            userDetails={userDetails}
          />
        ) : null}

        {gaugeDemand && selectedProperty ? (
          <button
            type="button"
            className="gauge-demand-back"
            onClick={() => {
              setGaugeDemand(false);
              // In the mobile drawer this sheet covers the feed it just
              // switched back to, so leaving it open hides the result.
              onDismiss?.();
            }}
          >
            Show all buyers in this market
          </button>
        ) : null}
      </div>

      {/* Match tab demand insights - replaces dual-handle price slider */}
      <div
        className={`filter-properties-wrap mt-3 border-bottom-0${tourDemand ? " is-tour-sample" : ""}`}
        data-member-tour="sell-demand"
      >
        {showDemand ? (
          <h6>Demand insights</h6>
        ) : null}
        {showDemand ? (
          <p className="feed-section-note">
            {!isGitchaOwnerView(userType)
              ? "Want-Listings whose criteria match this property."
              : "Want-listings whose criteria match your home."}
          </p>
        ) : null}
        {tourDemand ? (
          <MatchDemandPanel
            selectedProperty={tourDemand.property}
            adType={BUY}
            totalCountMatch={tourDemand.count}
            adListGitchaGenie={tourDemand.list}
            isAgent
            loading={false}
          />
        ) : gaugeDemand && selectedProperty ? (
          <MatchDemandPanel
            selectedProperty={selectedProperty}
            adType={adType}
            totalCountMatch={totalCountMatch}
            adListGitchaGenie={adListGitchaGenie}
            isAgent={userType === USER_TYPE_AGENT}
            loading={demandLoading}
          />
        ) : deepLinkPending ? (
          <div
            className="match-property-skeleton shimmer"
            data-testid="deeplink-loading"
            role="status"
            aria-label="Loading matches"
          >
            <span className="visually-hidden">Loading matches…</span>
          </div>
        ) : unresolvedLink && targetInvId != null && !selectedProperty ? (
          <p className="feed-section-note" data-testid="deeplink-unresolved">
            We couldn&apos;t find that property. Pick one from the list above to
            see its matches.
          </p>
        ) : (
          <DemandTeaser
            ownerView={isGitchaOwnerView(userType)}
            hasProperties={allFetched ? Boolean(allProperties?.length) : null}
          />
        )}
      </div>
    </>
  );
};
export default LeftpanelTabContentRight;
